// @spec BCP-STR-034, BCP-STR-037
'use client';

import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, Package } from 'lucide-react';
import * as React from 'react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { useRecoveryResourcesList } from '@/features/bcp/api/resources';
import { RecoveryResourceEmptyState } from '@/features/bcp/components/RecoveryResourceEmptyState';
import { RecoveryResourceFilters } from '@/features/bcp/components/RecoveryResourceFilters';
import { RecoveryResourceTable } from '@/features/bcp/components/RecoveryResourceTable';
import { ResourceStatusUpdateDialog } from '@/features/bcp/components/ResourceStatusUpdateDialog';
import type {
  RecoveryResource,
  RecoveryResourceFiltersState,
  RecoveryResourceSortField,
} from '@/features/bcp/types/resources';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

const resourceListSearchSchema = z.object({
  page: z.coerce.number().optional().default(1),
  pageSize: z.coerce.number().optional().default(20),
  resourceType: z
    .enum(['alternate_site', 'equipment', 'personnel', 'vendor'])
    .optional(),
  status: z
    .enum(['available', 'partially_available', 'unavailable'])
    .optional(),
  search: z.string().optional(),
  sortBy: z
    .enum(['created_at', 'updated_at', 'name', 'resource_type'])
    .optional()
    .default('created_at'),
  sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
});

type ResourceListSearch = z.infer<typeof resourceListSearchSchema>;

export const Route = createFileRoute('/_authenticated/bcp/resources/')({
  validateSearch: resourceListSearchSchema,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.recoveryResources'),
  }),
  component: RecoveryResourcesListPage,
});

function RecoveryResourcesListPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const search: ResourceListSearch = useSearch({ from: '/_authenticated/bcp/resources/' });
  const { organizations, isLoading: orgsLoading } = useOrganizations();

  const activeOrg = organizations.find((org) => org.isActive);

  const filters: RecoveryResourceFiltersState = React.useMemo(
    () => ({
      resourceType: search.resourceType ?? null,
      status: search.status ?? null,
      search: search.search ?? null,
      sortBy: search.sortBy as RecoveryResourceSortField,
      sortOrder: search.sortOrder,
    }),
    [search],
  );

  const {
    data: resourcesData,
    isLoading: resourcesLoading,
    isFetching: resourcesFetching,
  } = useRecoveryResourcesList({
    page: search.page,
    pageSize: search.pageSize,
    ...filters,
    enabled: Boolean(activeOrg),
  });

  const handleFiltersChange = React.useCallback(
    (newFilters: Partial<RecoveryResourceFiltersState>) => {
      const merged = { ...filters, ...newFilters };
      const searchUpdate: ResourceListSearch = {
        ...search,
        resourceType: merged.resourceType ?? undefined,
        status: merged.status ?? undefined,
        search: merged.search ?? undefined,
        sortBy: merged.sortBy,
        sortOrder: merged.sortOrder,
        page: 1,
      };
      void navigate({
        search: searchUpdate as never,
        replace: true,
      });
    },
    [navigate, search, filters],
  );

  const handlePageChange = React.useCallback(
    (page: number) => {
      void navigate({
        search: { ...search, page } as never,
        replace: true,
      });
    },
    [navigate, search],
  );

  const handleSort = React.useCallback(
    (field: RecoveryResourceSortField) => {
      const newOrder =
        filters.sortBy === field && filters.sortOrder === 'asc' ? 'desc' : 'asc';
      handleFiltersChange({
        sortBy: field,
        sortOrder: newOrder,
      });
    },
    [filters, handleFiltersChange],
  );

  const handleRowClick = React.useCallback(
    (_resourceId: string) => {
      // TODO: Navigate to resource detail page when implemented
    },
    [],
  );

  const [statusUpdateResource, setStatusUpdateResource] = React.useState<RecoveryResource | null>(
    null,
  );

  const handleStatusUpdate = React.useCallback((resource: RecoveryResource) => {
    setStatusUpdateResource(resource);
  }, []);

  const handleClearFilters = React.useCallback(() => {
    handleFiltersChange({
      resourceType: null,
      status: null,
      search: null,
      sortBy: 'created_at',
      sortOrder: 'desc',
    });
  }, [handleFiltersChange]);

  if (orgsLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 rounded bg-muted" />
            <div className="h-12 rounded bg-muted" />
            <div className="h-64 rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  if (!activeOrg) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-lg border border-dashed p-12 text-center">
            <h2 className="text-lg font-semibold text-foreground">{t('common:routeErrors.noOrgSelected')}</h2>
            <p className="mt-2 text-muted-foreground">
              {t('common:routeErrors.noOrgSelectedDesc')}
            </p>
            <Button
              variant="outline"
              onClick={() => void navigate({ to: '/settings/organizations' })}
              className="mt-4"
            >
              {t('common:routeErrors.goToOrganizations')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const hasFilters = Boolean(filters.resourceType ?? filters.status ?? filters.search);
  const pagination = resourcesData?.pagination;
  const totalPages = pagination?.totalPages ?? 1;
  const currentPage = pagination?.page ?? 1;

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-3xl font-semibold text-foreground">Recovery Resources</h1>
              <p className="mt-1 text-muted-foreground">
                Manage recovery resources for {activeOrg.name}
              </p>
            </div>
          </div>
        </header>

        {/* Filter Bar */}
        <RecoveryResourceFilters
          filters={filters}
          typeCounts={resourcesData?.typeCounts}
          onFiltersChange={handleFiltersChange}
        />

        {/* Loading state */}
        {resourcesLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : resourcesData?.items.length === 0 ? (
          <RecoveryResourceEmptyState
            hasFilters={hasFilters}
            onClearFilters={handleClearFilters}
          />
        ) : (
          <>
            {/* Resource table */}
            <RecoveryResourceTable
              resources={resourcesData?.items ?? []}
              sortBy={filters.sortBy}
              sortOrder={filters.sortOrder}
              onSortChange={handleSort}
              onRowClick={handleRowClick}
              onStatusUpdate={handleStatusUpdate}
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t pt-4">
                <p className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages} ({pagination?.totalItems ?? 0} resources)
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handlePageChange(currentPage - 1);
                    }}
                    disabled={currentPage <= 1 || resourcesFetching}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handlePageChange(currentPage + 1);
                    }}
                    disabled={currentPage >= totalPages || resourcesFetching}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
        {/* Status Update Dialog */}
        {statusUpdateResource && (
          <ResourceStatusUpdateDialog
            resource={statusUpdateResource}
            open={!!statusUpdateResource}
            onOpenChange={(open) => {
              if (!open) setStatusUpdateResource(null);
            }}
          />
        )}
      </div>
    </div>
  );
}
